// focus.ts — Resolve the single focused-entity id into the typed view the renderer
// and camera need. The game state holds ONE id (the deepest focused entity — a
// system, a planet, or a point of singularity); everything else (which system is
// active, whether a planet or gate is focused) is derived from it here, so there is
// a single source of truth instead of three parallel id slots.
//
// Entity ids are globally unique (the generator scopes planet/moon ids by system),
// and a singularity's id is the composite `${systemId}>${neighborId}` built from the
// hyperlane it fronts. Systems and planets are looked up in the galaxy; a gate id is
// recognised by its `>` and split.
import type { Galaxy } from '../types';

export interface Focus {
  /** The active system (the one whose detail renders), or null at the galaxy level. */
  systemId: string | null;
  /** The focused planet (the moon's parent when a moon is focused), or null. */
  planetId: string | null;
  /** The focused moon within that planet, or null. */
  moonId: string | null;
  /** The focused point of singularity (`${systemId}>${neighborId}`), or null. */
  singularityId: string | null;
}

const NONE: Focus = { systemId: null, planetId: null, moonId: null, singularityId: null };

/** Resolve a single focused-entity id into its system / planet / moon / singularity view. */
export function resolveFocus(galaxy: Galaxy | null, focusedId: string | null): Focus {
  if (!galaxy || !focusedId) {
    return NONE;
  }
  // Point of singularity: `${systemId}>${neighborId}`.
  const gt = focusedId.indexOf('>');
  if (gt >= 0) {
    return { systemId: focusedId.slice(0, gt), planetId: null, moonId: null, singularityId: focusedId };
  }
  // A system itself.
  if (galaxy.systems.some((s) => s.id === focusedId)) {
    return { systemId: focusedId, planetId: null, moonId: null, singularityId: null };
  }
  // A planet or one of its moons — find the parent chain.
  for (const s of galaxy.systems) {
    for (const p of s.planets) {
      if (p.id === focusedId) {
        return { systemId: s.id, planetId: focusedId, moonId: null, singularityId: null };
      }
      if ((p.moons ?? []).some((m) => m.id === focusedId)) {
        return { systemId: s.id, planetId: p.id, moonId: focusedId, singularityId: null };
      }
    }
  }
  // Unknown id (e.g. stale) → treat as the galaxy level.
  return NONE;
}

/** The id to focus when stepping OUT one level: a moon returns to its planet; a
 *  planet or gate returns to its system; a system returns to the galaxy (null). */
export function focusParent(galaxy: Galaxy | null, focusedId: string | null): string | null {
  const f = resolveFocus(galaxy, focusedId);
  if (f.moonId) {
    return f.planetId;
  }
  if (f.planetId || f.singularityId) {
    return f.systemId;
  }
  return null;
}
